import React, {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Button, Dialog, Input, NavBar, Skeleton, TextArea, Toast} from 'antd-mobile';
import dayjs from 'dayjs';
import { getNavBarContainerStyle, getBottomNavBarContainerStyle } from "../../../utils/deviceUtils";


import EmptyState from "../../../components/EmptyState";
import {t} from "../../../i18n";
import pickedUpDataService, {
    PickedUpData,
    uploadPhotosWithTimeout
} from "../../../services/PickedUpDataService";
import pickUpPhotoService, {
    PickUpPhotoType,
    PickUpPositionType,
    PICKUP_FILE_PHOTO_SLOTS
} from "../services/PickUpPhotoService";
import PickUpPhotoViewer from "../components/PickUpPhotoViewer";
import PickUpPhotoUploadCard from "../components/PickUpPhotoUploadCard";
import {PickUpNativePermissions} from "../components/PickUpPhotoNativeBridge";
import PickUpInfoItem from "../components/PickUpInfoItem";
import {PickupAppointmentType} from "../graphql/types/PickupTaskWithAppointmentsType";
import {buildAddressFromAppointment} from "../../../utils/addressUtils";
import {useCompletedPickupTaskAppointment, usePickupTaskOperations} from '../store/pickupStore';
import {getBusinessDaysText, getUnitLabel, isOnsiteContactApplicable, getTodayBusinessHoursText} from "../../../enums/PickupEnum";
import {useTranslation} from "react-i18next";
import {PickupFinishedPath} from "../../../enums/PickUpNavigatePathEnum";
import {PhoneFill} from "antd-mobile-icons";
import {parseExceptionMessageKey} from "../../../utils/exceptionUtils";
import {usePickupSiteV2Gray} from "../../../hooks/usePickupSiteV2Gray";

interface PickedUpPageProps {}

const MIN_PICKUP_FILE_PHOTOS = 3;

const PickedUpFailedToSecPage: React.FC<PickedUpPageProps> = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const appointmentId = location.state?.appointmentId;
    const taskId = location.state?.taskId;
    const selectedUnit = location.state?.selectedUnit;
    const selectedDate = location.state?.selectedDate;
    const { i18n } = useTranslation();
    const [photos, setPhotos] = useState<Record<PickUpPositionType, PickUpPhotoType | undefined>>({} as Record<PickUpPositionType, PickUpPhotoType | undefined>);
    const [viewingPhoto, setViewingPhoto] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [pickingUpData, setPickingUpData] = useState<PickupAppointmentType>();
    const [actualPickupCount, setActualPickupCount] = useState<string>('');

    // 揽收站点 2.0：本页是「已完成列表 → 揽收失败单 → 重新操作揽收成功」入口，
    // 与 PickedUpPage（正常揽收成功）是两个独立页面，2.0 的现场联系人模块此前只加在后者，
    // 导致这条路径漏采现场联系人。口径与 PickedUpPage 一致：灰度 hub 展示 + 必填。
    const { express2Enabled } = usePickupSiteV2Gray();
    const siteV2Enabled = express2Enabled(pickingUpData?.warehouseId);
    // 建联状态门控口径与 PickedUpPage 一致：只对待上门/上门中/揽收成功待建联/揽收失败待建联的站点采集。
    // 本页是「重新操作揽收失败单」入口，站点大概率已经是 3(揽收失败待建联)，仍在采集范围内。
    const showOnSiteContact = siteV2Enabled && isOnsiteContactApplicable(pickingUpData?.linkStatus);
    const [onSiteContactName, setOnSiteContactName] = useState<string>('');
    const [onSiteContactPhone, setOnSiteContactPhone] = useState<string>('');
    const [onSiteNote, setOnSiteNote] = useState<string>('');
    const onSitePhoneValid = /^\d{10}$/.test(onSiteContactPhone);
    const onSiteContactValid = !showOnSiteContact || (onSiteContactName.trim() !== '' && onSitePhoneValid);
    const {pickupTaskAppointment,loading} = useCompletedPickupTaskAppointment(appointmentId, taskId);
    const { updateCompletedPickupAppointment } = usePickupTaskOperations();

    useEffect(() => {
        if (pickupTaskAppointment) {
            setPickingUpData(pickupTaskAppointment);
        }
    }, [pickupTaskAppointment]);

    useEffect(() => {
        const requestPermissions = async () => {
            try {
                await PickUpNativePermissions.request('camera');
                await PickUpNativePermissions.request('storage');
            } catch (error) {
                console.error('Failed to request permissions:', error);
            }
        };

        requestPermissions();
    }, []);

    // 保存订单数据到本地
    const savePickedUpToLocal = async (): Promise<boolean> => {

        try {
            const pickedUpData: PickedUpData = {
                updatedAt: Date.now(),
                photos: Object.entries(photos || {})
                    .map(([type, photo]) => ({
                    type: type as PickUpPositionType,
                    photo: photo as PickUpPhotoType
                })),
                id: pickingUpData?.id?.toString() || '',
                type: 'failedToSec',
                stopNumber: pickingUpData?.sequence?.toString(),
                channelName: pickingUpData?.channelName,
                actualPickupCount: parseInt(actualPickupCount) || 0,
                actualPickupUnit: selectedUnit || 0,
                taskId: pickingUpData?.taskId,
                cooperationWarehouseId: pickingUpData?.cooperationWarehouseId,
                // 现场联系人此前不入离线队列（failedToSec 的重传通路压根没带它），弱网下 100% 丢失。
                // 现在它随主请求一起重传，必须先存下来
                onSiteContact: {
                    name: onSiteContactName.trim(),
                    phone: onSiteContactPhone,
                    note: onSiteNote.trim(),
                },
            };

            // 保存到本地
            const saved = await pickedUpDataService.savePickedUpData(pickedUpData);
            return saved;
        } catch (error) {
            console.error('Failed to save PickedUp to local:', error);
            return false;
        }
    };

    /*照片相关操作 start======*/
    const handleCall = () => {
        if (!pickingUpData?.contactPhone || pickingUpData.contactPhone.trim() === '') {
            Toast.show({
                content: t('pages.delivery.orderDetail.noPhoneNumber'),
                position: 'bottom',
            });
            return;
        }

        // 清理电话号码，只保留数字和+号
        const cleanPhone = pickingUpData.contactPhone.replace(/[^\d+]/g, '');

        if (!cleanPhone) {
            Toast.show({
                content: t('pages.delivery.orderDetail.invalidPhoneNumber'),
                position: 'bottom',
            });
            return;
        }

        try {
            window.location.href = `tel:${cleanPhone}`;
        } catch (error) {
            console.error('Phone call failed:', error);
            Toast.show({
                content: t('pages.delivery.orderDetail.phoneCallFailed', {phone: cleanPhone}),
                position: 'bottom',
                duration: 3000
            });
        }
    };

    // 拍照或从相册选择
    const takePhoto = async (type: PickUpPositionType, file: File) => {
        try {
            setIsLoading(true);
            await pickUpPhotoService.handlePhotoNotUpload(type, file, (updateFn) => {
                setPhotos((prev) => updateFn(prev));
            });
        } catch (error) {
            console.error('Failed to take photo:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // 删除照片
    const deletePhoto = (type: PickUpPositionType) => {
        Dialog.confirm({
            content: t('pages.pickUp.photo.deleteContent'),
            confirmText: t('pages.pickUp.photo.deleteConfirm'),
            cancelText: t('pages.pickUp.photo.deleteCancel'),
            onConfirm: () => {
                pickUpPhotoService.handleDeletePhoto(type, (updateFn) => {
                    setPhotos((prev) => updateFn(prev));
                });
            },
        });
    };

    // 查看大图
    const handleViewPhoto = (url: string) => setViewingPhoto(url);

    /*照片相关操作 end======*/

    // 揽收成功
    const handleCompletePickedUp = async () => {
        setIsLoading(true);
        try {
            const selectedPhotos = PICKUP_FILE_PHOTO_SLOTS
                .map((slot) => photos[slot])
                .filter((item): item is PickUpPhotoType => item?.objectKey !== undefined && item?.url !== undefined);

            if (selectedPhotos.length < MIN_PICKUP_FILE_PHOTOS) {
                Toast.show({ content: t('pages.pickUp.pickedUp.takeAtLeastPhotos', {count: MIN_PICKUP_FILE_PHOTOS}), position: 'center' });
                return;
            }
            const currentPhotos = {...photos};

            await updateCompletedPickupAppointment({
                appointmentId: pickingUpData?.id ||  '',
                taskId: pickingUpData?.taskId || '0',
                actualParcelCount: parseInt(actualPickupCount) || 0,
                actualParcelUnit: selectedUnit,
                photosS3KeysJson: selectedPhotos.map((photo) => photo.objectKey),
                actualPickupDate: dayjs().format("YYYY-MM-DD"),
                actualPickupTimeEnd: dayjs().format("HH:mm:ss"),
                cooperationWarehouseId: pickingUpData?.cooperationWarehouseId,
            },true  );
            setPhotos(currentPhotos);

            // 图片上传，带超时控制
            const photoKeys = await uploadPhotosWithTimeout(selectedPhotos);

            await updateCompletedPickupAppointment({
                appointmentId: pickingUpData?.id ||  '',
                taskId: pickingUpData?.taskId || '0',
                actualParcelCount: parseInt(actualPickupCount) || 0,
                actualParcelUnit: selectedUnit,
                photosS3KeysJson: photoKeys,
                actualPickupDate: dayjs().format("YYYY-MM-DD"),
                actualPickupTimeEnd: dayjs().format("HH:mm:ss"),
                cooperationWarehouseId: pickingUpData?.cooperationWarehouseId,
                // 揽收站点 2.0 现场回传：并入主请求，不再单发 submitPickupOnsiteFeedback（原因同 PickedUpPage）。
                // 本页是「失败改判成功」，同样属于司机现场见到人的场景。
                // 注意：站点若已因先前的揽收失败停在「揽收失败待建联」，服务端会静默跳过流转
                //（状态机没有该出向）——站点仍在跟进列表里，运营的下一步动作不变。
                onsiteContactName: onSiteContactName.trim() || undefined,
                onsiteContactPhone: onSiteContactPhone || undefined,
                onsiteNote: onSiteNote.trim() || undefined,
            },false  );

            navigate(PickupFinishedPath.PICKUP_FINISHED, {
                state: {
                    selectedDate: selectedDate
                }
            });
        } catch (error: any) {
            if (error.networkError) {
                await savePickedUpToLocal();
                navigate(PickupFinishedPath.PICKUP_FINISHED, {
                    state: {
                        selectedDate: selectedDate
                    }
                });
            } else {
                console.error('Failed to complete picked up:', error);
                const messageKey = parseExceptionMessageKey(error);
                if (messageKey === 'errors.pickup.cancelledAppointmentDotUpdate'){
                    Toast.show({ content: t(messageKey), position: 'center' });
                }else {
                    Toast.show({ content: 'Pickup failed, please try again', position: 'center' });
                }
            }
        } finally {
            setIsLoading(false);
        }
    };

    if (loading){
        return (
            <div className="flex flex-col h-screen">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={() => navigate(-1)}>
                        {t('pages.pickUp.pickedUp.title')}
                    </NavBar>
                </div>
                <Skeleton.Title animated />
                <Skeleton.Paragraph lineCount={5} animated />
                <Skeleton.Title animated />
                <Skeleton.Paragraph lineCount={3} animated />
                <Skeleton.Title animated />
                <Skeleton.Paragraph lineCount={1} animated />
                <Skeleton style={{width: '100%',height: '30%'}} animated />
            </div>
        );
    }

    if (!pickingUpData) {
        return (
            <div className="flex flex-col h-screen">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={() => navigate(-1)}>
                        {t('pages.pickUp.pickedUp.title')}
                    </NavBar>
                </div>
                <EmptyState description={t(`pages.pickUp.noPickUpInfo`)}/>
            </div>
        );
    }

    const businessDaysStr = getBusinessDaysText(pickingUpData.businessDays, i18n.language);
    return (
        <div className="flex flex-col h-screen">
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={() => navigate(-1)}>{t('pages.pickUp.pickedUp.title')}</NavBar>
            </div>

            <div className="flex-1 overflow-y-auto">
                {/* 揽收点信息 */}
                <div className="mb-1 bg-white rounded-lg p-4 shadow-sm">
                    <h2 className="text-base font-semibold mb-1">{t('pages.pickUp.pickedUp.information')}</h2>
                    <div className="mb-1">
                        <PickUpInfoItem label={t('pages.pickUp.pickedUp.name')} value={pickingUpData?.channelName} />
                        <PickUpInfoItem label={t('pages.pickUp.pickedUp.number')} value={pickingUpData?.sequence.toString()} />
                        <PickUpInfoItem label={t('pages.pickUp.pickedUp.contact')} value={pickingUpData?.contactName} />
                        <PickUpInfoItem label={t('pages.pickUp.pickedUp.phone')} value={pickingUpData?.contactPhone} />
                        <PickUpInfoItem label={t('pages.pickUp.pickedUp.address')} value={buildAddressFromAppointment(pickingUpData)} />
                        <PickUpInfoItem label={t('pages.pickUp.pickedUp.appointmentPackages')} value={`${pickingUpData?.quantity} ${getUnitLabel(Number(pickingUpData?.unit), i18n.language)}`} />
                        {/* 2.2 需求点3：拆成两行、各带标签，理由见 PickingUpDetailPage 同处注释 */}
                        <PickUpInfoItem label={t('pages.pickUp.pickedUp.workdays')} value={businessDaysStr}/>
                        <PickUpInfoItem
                            label={t('pages.pickUp.pickedUp.todayBusinessHours')}
                            value={getTodayBusinessHoursText(pickingUpData, t('pages.pickUp.pickedUp.closedToday'))}/>
                        <PickUpInfoItem label={t('pages.pickUp.pickedUp.appointmentTime')} value={pickingUpData?.appointmentTime} />
                        <PickUpInfoItem label={t('pages.pickUp.pickedUp.remark')} value={pickingUpData?.remark || '-'} />
                    </div>
                </div>
                <div className="bg-white rounded-lg p-4 ">
                    <h2 className="text-base font-semibold mb-1">{t('pages.pickUp.pickedUp.actualPickup')}</h2>
                    <div className="grid grid-cols-6 mb-1 items-center">
                        <div className="col-span-5 text-sm px-1 break-all">
                            <Input
                                placeholder={t('pages.pickUp.pickedUp.actualPickupPlaceholder')}
                                value={actualPickupCount}
                                onChange={setActualPickupCount}
                                type={'number'}
                                className="border border-gray-300 rounded-md h-10"
                            />
                        </div>
                        <div className="col-span-1 text-sm px-1 break-all">
                            <h2 className="text-base font-semibold">{getUnitLabel(selectedUnit, i18n.language)}</h2>
                        </div>
                    </div>
                </div>
                {/* 现场联系人信息（揽收站点 2.0；与 PickedUpPage 同结构同口径，灰度 hub + 建联流程中的站点展示、必填） */}
                {showOnSiteContact && (
                    <div className="mb-1 bg-white rounded-lg p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-2">
                            <h2 className="text-base font-semibold">{t('pages.pickUp.onSiteContact.title')}</h2>
                            <span className="text-xs text-green-600 bg-green-50 rounded px-1.5 py-0.5">
                                {t('pages.pickUp.onSiteContact.new')}
                            </span>
                        </div>
                        <div className="mb-2">
                            <div className="text-sm mb-1">
                                {t('pages.pickUp.onSiteContact.contactName')} <span className="text-red-500">*</span>
                            </div>
                            <Input
                                placeholder={t('pages.pickUp.onSiteContact.contactNamePlaceholder')}
                                value={onSiteContactName}
                                onChange={setOnSiteContactName}
                                className="border border-gray-300 rounded-md h-10 px-2"
                            />
                        </div>
                        <div>
                            <div className="text-sm mb-1">
                                {t('pages.pickUp.onSiteContact.contactPhone')} <span className="text-red-500">*</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600 border border-gray-300 rounded-md h-10 flex items-center px-2">+1</span>
                                <Input
                                    placeholder={t('pages.pickUp.onSiteContact.contactPhonePlaceholder')}
                                    value={onSiteContactPhone}
                                    onChange={(v) => setOnSiteContactPhone(v.replace(/\D/g, '').slice(0, 10))}
                                    type="tel"
                                    className="border border-gray-300 rounded-md h-10 px-2 flex-1"
                                />
                            </div>
                            {onSiteContactPhone !== '' && !onSitePhoneValid && (
                                <div className="text-xs text-red-500 mt-1">{t('pages.pickUp.onSiteContact.phoneInvalid')}</div>
                            )}
                        </div>
                        <div className="mt-2">
                            <div className="text-sm mb-1">{t('pages.pickUp.onSiteContact.note')}</div>
                            <TextArea
                                placeholder={t('pages.pickUp.onSiteContact.notePlaceholder')}
                                value={onSiteNote}
                                onChange={setOnSiteNote}
                                maxLength={200}
                                rows={2}
                                className="border border-gray-300 rounded-md px-2 py-1"
                            />
                        </div>
                    </div>
                )}
                {/*上传图片*/}
                <div className="mb-2 bg-white rounded-lg p-4 shadow-sm">
                    <h2 className="text-base font-semibold mb-1">{t('pages.pickUp.pickedUp.photos')}</h2>
                    <p className="text-sm text-gray-600 mb-1">
                        {t('pages.pickUp.pickedUp.takeAtLeastPhotos', {count: MIN_PICKUP_FILE_PHOTOS})}
                    </p>
                    <div className="grid grid-cols-3 gap-2 my-4">
                        {PICKUP_FILE_PHOTO_SLOTS.map((slot) => (
                            <PickUpPhotoUploadCard
                                key={slot}
                                photoUrl={photos[slot]?.url}
                                type={slot}
                                description={``}
                                onTakePhoto={takePhoto}
                                onDelete={deletePhoto}
                                onView={handleViewPhoto}
                            />
                        ))}
                    </div>
                </div>

                {/* 查看大图 */}
                <PickUpPhotoViewer
                    photoUrl={viewingPhoto}
                    onClose={() => setViewingPhoto(null)}
                />
            </div>

            {/* 提交按钮 */}
            <div {...getBottomNavBarContainerStyle('flex-0 flex px-4 py-2 mt-auto gap-4')}>
                <div className="flex-0">
                    <Button
                        shape="rounded"
                        className="w-12 h-12 flex items-center justify-center"
                        onClick={handleCall}
                        style={{'--background-color': '#f0f0f0'}}
                    >
                        <PhoneFill className="text-xl"/>
                    </Button>
                </div>

                <Button
                    block
                    color="primary"
                    loading={isLoading}
                    disabled={PICKUP_FILE_PHOTO_SLOTS.filter((slot) => photos[slot]?.url).length < MIN_PICKUP_FILE_PHOTOS || isLoading || !actualPickupCount || !onSiteContactValid}
                    onClick={handleCompletePickedUp}
                >
                    {t('pages.pickUp.pickedUp.complete')}
                </Button>
            </div>
        </div>
    );
};

export default PickedUpFailedToSecPage;
